import {
  ApiResponse,
  ResponseError,
  HTTPHeaders,
  BaseAPI,
  Configuration,
} from '@spotify-internal/ads-api-typescript-client-development';
import { useTransport } from 'contexts/TransportContext';
import { useState, useCallback } from 'react';

import { getAdsApiResponse } from 'utils/api/get-ads-api-response';
import { getAdsApiConfig } from 'utils/api/get-ads-api-config';
import { getApiPartnerBaseUrl } from 'utils/api/get-api-partner-base-url';
import * as Sentry from '@sentry/react';
import getConfig from 'next/config';
import { useGoogleReCaptcha } from './use-google-recaptcha';

const { publicRuntimeConfig } = getConfig() || {};

// Define a type that extends BaseAPI and allows accessing methods by string
interface ApiWithMethods extends BaseAPI {
  [key: string]: any;
}

/* NOTE: Also update changes to the corresponding useAdsApiInstance hook in voltron (campaignManager) */

/**
 * Uniformly execute all Ads API requests and process the responses,
 * handling errors to make sure the error message propagates up to the query.
 *
 * @param request The individual request function to be called (example "apiInstance.getBusinessRaw({ businessId })"). This *must* use the "Raw" version.
 * @param jsonProcessor Corresponding JSON mapper function to be called, every Ads API endpoint comes with one (example "businessResponseFromJSON")
 */
const runApiRequest = async <K, R>(
  request: Promise<ApiResponse<K>>,
  // eslint-disable-next-line
  jsonProcessor: (json: any) => R,
) => {
  try {
    const response = await request;
    return jsonProcessor(response.raw.body);
  } catch (error) {
    const errorMessage = getAdsApiResponse(error.response);

    // Create a unique error for testing (prevents deduplication)
    const timestamp = Date.now();
    const sentryError = new Error(
      `Ads API Request Failed [${timestamp}]: ${
        typeof errorMessage === 'string'
          ? errorMessage
          : errorMessage?.detail || 'Unknown error'
      }`,
    );

    Sentry.captureException(sentryError, {
      level: 'error',
      tags: {
        error_source: 'ads_api_request',
        sp_trace_id: error.response?.body?.sp_trace_id,
      },
      extra: {
        sp_trace_id: error.response?.body?.sp_trace_id,
        url: error.response?.url,
      },
    });
    throw new ResponseError(
      error.response,
      JSON.stringify(getAdsApiResponse(error.response)),
    );
  }
};

/**
 * Helper to instantiate Ads API TS client and run API requests,
 * uniformly handling responses and errors.
 *
 * @param ApiContext The class of API to be used (example "BusinessesApi")
 * @param basePath The base URL for the API. Defaults to the v2 base URL (`v2BasePath`). Use this parameter to specify a different API version if needed.
 * @returns An object containing:
 *  - `runApiRequest`: A function for executing API requests using the client.
 *  - `requestParams`: Keep track of the last request parameters for recreating with a new API instance
 */
export const useAdsApiInstance = (
  // eslint-disable-next-line
  ApiContext: new (config: Configuration) => ApiWithMethods,
  basePath?: string,
) => {
  const baseUrl = getApiPartnerBaseUrl();
  const transport = useTransport();
  const { executeRecaptcha } = useGoogleReCaptcha();

  // Create the initial API instance
  const [apiInstance, setApiInstance] = useState<ApiWithMethods>(() => {
    const adsApiConfig = getAdsApiConfig(transport, basePath || baseUrl);
    return new ApiContext(adsApiConfig) as ApiWithMethods;
  });

  // Create a wrapped version of runApiRequest that includes the reCAPTCHA token
  const wrappedRunApiRequest = useCallback(
    async <R>(
      // eslint-disable-next-line
      jsonProcessor: (json: any) => R,
      requestParams: {
        method: string;
        params: Record<string, any>;
      },
    ) => {
      const { method, params } = requestParams;
      // If executeRecaptcha is available and we have the original request parameters
      // we can recreate the request with the reCAPTCHA token
      if (executeRecaptcha) {
        try {
          // Get the reCAPTCHA token
          const recaptchaToken = await executeRecaptcha('ads_api_request');

          // Create headers with reCAPTCHA token and non-prod flag if needed
          const headers: HTTPHeaders = {
            'X-Recaptcha-Token': recaptchaToken,
          };

          // Add non-prod header if not in master environment
          if (publicRuntimeConfig?.CONTENTFUL_ENVIRONMENT !== 'master') {
            headers['x-stargate-non-prod'] = 'true';
          }

          // Create a new API instance with the token in the headers
          const adsApiConfig = getAdsApiConfig(
            transport,
            basePath || baseUrl,
            headers,
          );

          const newApiInstance = new ApiContext(adsApiConfig) as ApiWithMethods;

          // Update the stored API instance for future requests
          setApiInstance(newApiInstance);

          // Use the new API instance to recreate the request with the same parameters
          // This ensures the request includes the reCAPTCHA token in its headers
          // @ts-ignore
          const newRequest = newApiInstance[method](params);

          // Run the new request
          return runApiRequest(newRequest, jsonProcessor);
        } catch (recaptchaError) {
          console.error('Failed to execute reCAPTCHA', recaptchaError);
          // Continue with the original request if we couldn't get a token
        }
      }

      return runApiRequest(apiInstance[method](params), jsonProcessor);
    },
    [executeRecaptcha, transport, basePath, baseUrl],
  );

  return { runApiRequest: wrappedRunApiRequest };
};
